import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Input, message, Modal, Select, Space, Table, Tag } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import IconGroup from "./IconGroup";
import { LoadingIcon } from "../../../components";
import {
  deleteGreenhouse,
  getCrops,
  getGreenhouses,
  postGreenhouse,
  putGreenhouse,
  getPlots,
  getVarieties,
} from "../../../utils/api";
import { UserContext } from "../../../context/UserContext";
import CreateModalContent from "./CreateModalContent";
import { COLORS } from "../../../consts";

const GreenhouseTable = ({
  greenhouseLabel,
  currentOrg,
  setCurrentOrg,
  orgData,
  currentSite,
  setCurrentSite,
  siteData,
}) => {
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [siteLoading, setSiteLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const { userData } = useContext(UserContext);

  // State for Create Modal
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);

  // State for Edit Modal
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [allCrops, setAllCrops] = useState([]);
  const [allPlots, setAllPlots] = useState([]);
  const [allVarieties, setAllVarieties] = useState([]);

  const renderBoolean = (bool) => (
    <p className={`${bool ? "text-primaryBlue" : "text-dangerRed"}`}>
      {String(bool)}
    </p>
  );

  // START - API Functions

  const fetchAllVariety = async () => {
    try {
      setDataLoading(true);
      const res = await getVarieties(currentOrg, currentSite);
      const fetchedData = res.data?.data?.map((variety) => ({
        value: variety.id,
        label: variety.name,
      }));
      setAllVarieties(fetchedData);
    } catch (err) {
      message.error("Error fetching Variety!");
    }
  };

  const fetchGreenhouses = async () => {
    try {
      setDataLoading(true);

      const res = await getGreenhouses(currentOrg, currentSite);
      const fetchedData = res.data?.data?.map((greenhouse) => {
        return {
          ...greenhouse,
          key: greenhouse.id,
          crop_display_name: greenhouse.crop?.display_name || "",
        };
      });

      setData(fetchedData);
    } catch (err) {
      message.error("Error fetching Greenhouses!");
    } finally {
      setDataLoading(false);
    }
  };

  const updateGreenhouse = async (id, data) => {
    try {
      await putGreenhouse(id, data);
      message.success(greenhouseLabel + " updated successfully!");
      fetchGreenhouses();
    } catch (err) {
      message.error("Error updating " + greenhouseLabel + "!");
    }
  };

  const createGreenhouse = async (data) => {
    try {
      const final_data = {
        ...data,
        organization: currentOrg,
        site: currentSite,
      };
      await postGreenhouse(final_data);
      message.success(greenhouseLabel + " created successfully!");
      setIsCreateModalVisible(false);
      fetchGreenhouses();
    } catch (err) {
      message.error("Error creating " + greenhouseLabel + "!");
    }
  };

  const fetchAllCrops = async () => {
    try {
      const res = await getCrops(currentSite);
      const cropsData = res.data.data
        .filter((crop) => crop.is_active)
        .map((crop) => ({
          value: crop.id,
          label: crop.display_name,
        }));
      setAllCrops(cropsData);
    } catch (err) {
      message.error("Error fetching Crops!");
    }
  };

  const deleteGreenhouseHandler = async (id) => {
    try {
      await deleteGreenhouse(id);
      message.success(greenhouseLabel + " deleted successfully!");
      fetchGreenhouses();
    } catch (err) {
      message.error("Error deleting " + greenhouseLabel + "!");
    }
  };

  const fetchAllPlots = async () => {
    try {
      const res = await getPlots(currentOrg, currentSite);
      const plotsData = res.data.data
        .filter((plot) => plot.is_active)
        .map((plot) => ({
          value: plot.id,
          label: plot.name,
        }));
      setAllPlots(plotsData);
    } catch (err) {
      message.error("Error fetching Plots!");
    }
  };

  // Fetch Greenhouse on Site Change
  useEffect(() => {
    if (currentSite) {
      fetchGreenhouses();
      fetchAllCrops();
      if (greenhouseLabel === "Glasshouse") {
        fetchAllPlots();
        fetchAllVariety();
      }
    }
  }, [currentSite, currentOrg, greenhouseLabel]);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: true,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#39f" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span>
          {text
            .toString()
            .split(new RegExp(`(${searchText})`, "gi"))
            .map((fragment, i) =>
              fragment.toLowerCase() === searchText.toLowerCase() ? (
                <span key={i} className="highlight">
                  {fragment}
                </span>
              ) : (
                fragment
              ),
            )}
        </span>
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
    },
    ...(greenhouseLabel !== "Glasshouse"
      ? [
          {
            title: "Rows",
            dataIndex: "num_of_rows",
            key: "rows",
            ...getColumnSearchProps("num_of_rows"),
          },
          {
            title: "Variety",
            dataIndex: "variety_grown",
            key: "variety",
            ...getColumnSearchProps("variety_grown"),
          },
          {
            title: "Under Spec Threshold",
            dataIndex: "under_spec_threshold",
            key: "under_spec_threshold",
            ...getColumnSearchProps("under_spec_threshold"),
            render: (value) => renderBoolean(value),
          },
          {
            title: "Over Spec Threshold",
            dataIndex: "over_spec_threshold",
            key: "over_spec_threshold",
            ...getColumnSearchProps("over_spec_threshold"),
            render: (value) => renderBoolean(value),
          },
        ]
      : [
          {
            title: "Crop",
            dataIndex: "crop_display_name",
            key: "crop_display_name",
            ...getColumnSearchProps("crop_display_name"),
            render: (cropName) =>
              cropName ? (
                <Tag color={COLORS.primaryBlue}>{cropName}</Tag>
              ) : (
                <Tag color="red">N/A</Tag>
              ),
          },
          {
            title: "Bays",
            dataIndex: "plots",
            key: "plots",
            render: (plots) =>
              plots && plots.length > 0 ? (
                <Space wrap>
                  {plots.map((plot) => (
                    <Tag color={COLORS.primaryBlue} key={plot.id}>
                      {plot.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                ""
              ),
          },
          {
            title: "Varieties",
            dataIndex: "varieties",
            key: "varieties",
            render: (varieties) =>
              varieties && varieties.length > 0 ? (
                <Space wrap>
                  {varieties.map((variety) => (
                    <Tag color={COLORS.primaryBlue} key={variety.id}>
                      {variety.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                ""
              ),
          },
        ]),
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className="text-successGreen" />
        ) : (
          <CloseCircleOutlined className="text-dangerRed" />
        ),
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <Space size="middle">
          <IconGroup
            ele={record}
            updateCallback={updateGreenhouse}
            deleteCallback={deleteGreenhouseHandler}
            greenhouseLabel={greenhouseLabel}
            allCrops={allCrops}
            allVarieties={allVarieties}
            allPlots={allPlots}
            currentSite={currentSite}
          />
        </Space>
      ),
    },
  ];

  // Search Handlers
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  // Define final columns with search props
  const finalColumns = columns;

  return (
    <div>
      {dataLoading ? (
        <LoadingIcon classname="absolute h-full top-[40vh]" height="200px" />
      ) : (
        <>
          <div
            className={`w-full pb-10 h-fit flex justify-between items-center`}
          >
            <div className="flex justify-start items-center">
              {userData?.is_superuser ? (
                <Select
                  className="min-w-[150px] mr-5"
                  placeholder="Select Organization"
                  value={currentOrg}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={setCurrentOrg}
                  options={orgData.map((org) => ({
                    value: org.key,
                    label: org.name,
                  }))}
                />
              ) : null}
              {!siteLoading ? (
                <Select
                  className="min-w-[150px] mr-5"
                  placeholder="Select Site"
                  value={currentSite}
                  onChange={setCurrentSite}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={siteData.map((site) => ({
                    value: site.id,
                    label: site.display_name,
                  }))}
                />
              ) : (
                <div className="w-[150px]">
                  <LoadingIcon height="30px" />
                </div>
              )}
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalVisible(true)}
            >
              Create {greenhouseLabel}
            </Button>
          </div>
          <Table
            dataSource={data}
            columns={finalColumns}
            pagination={{
              hideOnSinglePage: true,
              position: ["bottomCenter"],
            }}
          />
          {/* Create Modal */}
          <Modal
            title={"Create" + greenhouseLabel}
            visible={isCreateModalVisible}
            onCancel={() => setIsCreateModalVisible(false)}
            footer={null}
          >
            <CreateModalContent
              setOpen={setIsCreateModalVisible}
              allCrops={allCrops}
              createCallback={createGreenhouse}
              allPlots={allPlots}
              allVarieties={allVarieties}
              greenhouseLabel={greenhouseLabel}
              currentSite={currentSite}
            />
          </Modal>
        </>
      )}
    </div>
  );
};

export default GreenhouseTable;
